<script lang="ts" setup>
import { AppLink } from '#components'

export interface Props {
  editable?: boolean
  action?: 'navigate' | 'dialog'
}

const props = withDefaults(defineProps<Props>(), { editable: false, action: 'navigate' })

const { total, items } = useCart()
const { totalQuantity } = useAddToCart()

const miniCartDialog = useUiDialog('mini-cart-dialog')

const getElement = computed(() => {
  return props.action === 'dialog' ? 'button' : AppLink
})
</script>

<template>
  <div class="mini-cart">
    <component
      :is="getElement"
      :to="{ name: 'cart' }"
      class="mini-cart__link"
      :aria-label="$t('Basket')"
      type="button"
      @click.prevent="props.action === 'dialog' ? miniCartDialog.open() : null"
    >
      <NuxtIcon name="icon:shoppingcart" />
      <span class="mini-cart__amount">
        {{ totalQuantity ? totalQuantity : '0' }}
      </span>
      <span class="mini-cart__label">
        {{ $t('Basket') }}
      </span>
      <span class="mini-cart__price">
        {{ total }}
      </span>
    </component>

    <UiDialog
      v-if="props.action === 'dialog'"
      name="mini-cart-dialog"
      class="mini-cart-dialog"
      dialog-type="from-right"
    >
      <template #dialogHeader>
        <strong class="dialog__title">
          {{ $t('My shopping cart') }}
        </strong>
      </template>

      <div v-if="items?.length">
        <div class="mini-cart__amount">
          <span>{{ $tc('items', { count: totalQuantity, quantity: totalQuantity }) }} - </span>
          <AppLink
            to="/cart"
            data-testid="minicart-view-cart"
            @click="miniCartDialog.close"
          >
            <span>{{ $t('View cart') }}</span>
          </AppLink>
        </div>

        <div class="mini-cart__products">
          <CartLine
            v-for="item in items"
            :key="item.id"
            :item="item"
          />
        </div>

        <div class="mini-cart__total">
          <span>{{ $t('Cart subtotal') }}</span>
          <UiPrice
            class="mini-cart__total-price"
            :prices="{ regularPrice: total }"
          />
        </div>

        <div class="dialog__buttons">
          <AppLink
            to="/cart"
            class="mini-cart__cta"
            @click="miniCartDialog.close"
          >
            <span>{{ $t('View cart') }}</span>
          </AppLink>
        </div>
      </div>
      <div
        v-else
        class="mini-cart__empty"
      >
        {{ $t('Empty cart') }}
      </div>
    </UiDialog>
  </div>
</template>

<style>
@import url("~layers/components/Header/Cart.css");
</style>
